import React, { useEffect, useState } from "react";
import { REPORTES_API } from "../../api/reportesApi";

export default function ReportesComparativo() {
  const [file, setFile] = useState(null);
  const [uploadMsg, setUploadMsg] = useState("");
  const [dataMulti, setDataMulti] = useState({});
  const [loading, setLoading] = useState(false);

  // ===============================
  // ✅ MUNICIPIOS (NUEVO)
  // ===============================
  const [fileMunicipio, setFileMunicipio] = useState(null);
  const [anioMunicipio, setAnioMunicipio] = useState("2025");
  const [mesMunicipio, setMesMunicipio] = useState("03");
  const [uploadMunicipioMsg, setUploadMunicipioMsg] = useState("");

  useEffect(() => {
    cargarComparativoMulti();
  }, []);

  // ===============================
  // SUBIR EXCEL (COMPARATIVO)
  // ===============================
  const subirExcel = async () => {
    if (!file) {
      alert("Selecciona un archivo Excel");
      return;
    }

    try {
      setLoading(true);
      setUploadMsg("");

      // ✅ FORZAR MIME DE EXCEL (evita file.type vacío o incorrecto)
      const excelContentType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      // 1️⃣ PEDIR URL FIRMADA
      const resUrl = await fetch(
        `${REPORTES_API}/reportes/comparativo/upload-url`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            anio: "2025",
            fileName: file.name,
            contentType: excelContentType,
            size: file.size,
          }),
        }
      );

      if (!resUrl.ok) {
        const txt = await resUrl.text();
        throw new Error(`Error upload-url ${resUrl.status}: ${txt}`);
      }

      const { uploadUrl } = await resUrl.json();

      // 2️⃣ SUBIR A S3
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": excelContentType },
        body: file,
      });

      if (!putRes.ok) {
        const t = await putRes.text().catch(() => "");
        throw new Error(`Error subiendo el archivo a S3: ${putRes.status} ${t}`);
      }

      setUploadMsg("✅ Excel subido correctamente. Procesando datos…");

      setTimeout(() => {
        cargarComparativoMulti();
      }, 2000);
    } catch (err) {
      console.error("❌ Error subiendo/procesando:", err);
      alert("Error subiendo el archivo (revisa consola y CloudWatch)");
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // ✅ SUBIR EXCEL (MUNICIPIOS)
  // ===============================
  const subirExcelMunicipios = async () => {
    if (!fileMunicipio) {
      alert("Selecciona un archivo Excel de municipios");
      return;
    }

    try {
      setLoading(true);
      setUploadMunicipioMsg("");

      const excelContentType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      // 1️⃣ PEDIR URL FIRMADA (MUNICIPIOS)
      const resUrl = await fetch(
        `${REPORTES_API}/reportes/municipio/upload-url`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            anio: Number(anioMunicipio),
            mes: String(mesMunicipio).padStart(2, "0"),
            fileName: fileMunicipio.name,
            contentType: excelContentType,
            size: fileMunicipio.size,
          }),
        }
      );

      if (!resUrl.ok) {
        const txt = await resUrl.text();
        throw new Error(`Error municipio upload-url ${resUrl.status}: ${txt}`);
      }

      const { uploadUrl } = await resUrl.json();

      // 2️⃣ SUBIR A S3
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": excelContentType },
        body: fileMunicipio,
      });

      if (!putRes.ok) {
        const t = await putRes.text().catch(() => "");
        throw new Error(`Error subiendo municipios a S3: ${putRes.status} ${t}`);
      }

      setUploadMunicipioMsg(
        "✅ Excel de municipios subido correctamente. Procesando datos…"
      );

      setTimeout(() => {
        setUploadMunicipioMsg("✅ Municipios procesados correctamente.");
      }, 2000);
    } catch (err) {
      console.error("❌ Error subiendo/procesando municipios:", err);
      alert("Error subiendo municipios (revisa consola y CloudWatch)");
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // CARGAR COMPARATIVO MULTI
  // ===============================
  const cargarComparativoMulti = async () => {
    try {
      const res = await fetch(
        `${REPORTES_API}/reportes/comparativo/multi?anios=2024,2025,2026,2027`
      );

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt);
      }

      const json = await res.json();
      setDataMulti(json?.data || {});
    } catch (err) {
      console.error("❌ Error cargando comparativo:", err);
      setDataMulti({});
    }
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* HEADER */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Carga de Reportes</h1>
        <p className="text-sm text-slate-600 mt-1">
          Subida y verificación técnica de archivos Excel.
        </p>
      </div>

      {/* PANEL COMPARATIVO */}
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
              Archivo de carga
            </div>

            <label className="group flex items-center justify-between gap-4 px-4 py-3 rounded-xl border border-dashed border-slate-300 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-indigo-100 transition">
                  📄
                </div>

                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {file ? file.name : "Selecciona o arrastra tu archivo Excel"}
                  </div>
                  <div className="text-xs text-slate-500">
                    Formato permitido: .xlsx
                  </div>
                </div>
              </div>

              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
              />
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={subirExcel}
              disabled={!file || loading}
              className={[
                "h-11 px-6 rounded-xl font-semibold transition flex items-center gap-2",
                loading
                  ? "bg-indigo-400 text-white cursor-wait"
                  : "bg-indigo-600 text-white hover:bg-indigo-700",
                !file && "opacity-50 cursor-not-allowed",
              ].join(" ")}
            >
              {loading ? "Procesando..." : "⬆️ Subir Excel"}
            </button>

            <button
              onClick={cargarComparativoMulti}
              disabled={loading}
              className="h-11 px-5 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100 transition"
            >
              🔄 Recargar KPIs
            </button>
          </div>
        </div>

        {uploadMsg && (
          <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <span>✅</span>
            <span>{uploadMsg}</span>
          </div>
        )}
      </div>

      {/* PANEL MUNICIPIOS */}
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
              Reporte por municipio (mensual)
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Año
                </div>
                <input
                  type="number"
                  value={anioMunicipio}
                  onChange={(e) => setAnioMunicipio(e.target.value)}
                  className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white"
                  placeholder="2025"
                />
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Mes
                </div>
                <select
                  value={mesMunicipio}
                  onChange={(e) => setMesMunicipio(e.target.value)}
                  className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white"
                >
                  {Array.from({ length: 12 }).map((_, i) => {
                    const m = String(i + 1).padStart(2, "0");
                    return (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <label className="group flex items-center justify-between gap-4 px-4 py-3 rounded-xl border border-dashed border-slate-300 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-indigo-100 transition">
                  🏛️
                </div>

                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {fileMunicipio
                      ? fileMunicipio.name
                      : "Selecciona o arrastra tu Excel de municipios"}
                  </div>
                  <div className="text-xs text-slate-500">
                    Formato permitido: .xlsx
                  </div>
                </div>
              </div>

              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setFileMunicipio(e.target.files[0])}
                className="hidden"
              />
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={subirExcelMunicipios}
              disabled={!fileMunicipio || loading}
              className={[
                "h-11 px-6 rounded-xl font-semibold transition flex items-center gap-2",
                loading
                  ? "bg-indigo-400 text-white cursor-wait"
                  : "bg-indigo-600 text-white hover:bg-indigo-700",
                !fileMunicipio && "opacity-50 cursor-not-allowed",
              ].join(" ")}
            >
              {loading ? "Procesando..." : "⬆️ Subir Municipios"}
            </button>
          </div>
        </div>

        {uploadMunicipioMsg && (
          <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <span>✅</span>
            <span>{uploadMunicipioMsg}</span>
          </div>
        )}
      </div>

      {Object.keys(dataMulti || {}).length === 0 && (
        <div className="mt-6 p-6 bg-white rounded-xl border border-slate-200 text-slate-600">
          No hay datos para mostrar todavía.
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(dataMulti || {}).map(([anio, info]) => (
          <div
            key={anio}
            className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">
                  Año
                </div>
                <div className="text-lg font-bold text-slate-900">{anio}</div>
              </div>

              <div className="text-right">
                <div className="text-xs text-slate-500">Total anual</div>
                <div className="text-2xl font-bold text-slate-900">
                  {info?.total?.toLocaleString("es-MX") ?? 0}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {(info?.meses || []).map((m) => (
                <div
                  key={m.mes}
                  className="rounded-lg border border-slate-100 bg-slate-50 p-3"
                >
                  <div className="text-xs text-slate-500 uppercase">
                    Mes {String(m.mes).padStart(2, "0")}
                  </div>
                  <div className="text-lg font-semibold text-slate-900">
                    {Number(m.valor || 0).toLocaleString("es-MX")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
